// Import external
import { Component, forwardRef, Input, OnDestroy, OnInit } from '@angular/core';
import { ControlValueAccessor, FormControl, NG_VALIDATORS, NG_VALUE_ACCESSOR } from '@angular/forms';
import * as _ from 'lodash';

import { User } from '@data/models/user.model';
import { UserService } from '@data/services/user.service';

// Import internal
@Component({

    selector: 'syn-users-picker',
    templateUrl: 'users-picker.component.html',
    styleUrls: ['users-picker.component.scss'],
    providers: [
        { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => UsersPickerComponent), multi: true },
        { provide: NG_VALIDATORS, useExisting: forwardRef(() => UsersPickerComponent), multi: true }
    ]
})
export class UsersPickerComponent implements ControlValueAccessor, OnInit, OnDestroy {

    @Input() placeholder: any;
    @Input() isBot = false;
    @Input() required = false;

    public userIdsControl: FormControl;
    public users: User[];
    public _userIds: string[] = [];
    public userId = '';

    // chips
    public selectable = false;
    public disabled = false;
    public removable = true;
    public addOnBlur = true;

    private sub: any;

    private _userType: string;
    private _accountType: string;

    constructor(private userService: UserService) {
        this.userIdsControl = new FormControl();
    }

    // TODO: use tags to distinguish service from user account
    @Input()
    get userType() {
        return this._userType;
    }

    @Input()
    get accountType() {
        return this._accountType;
    }

    @Input('value')
    get userIds() {
        return this._userIds;
    }

    set userType(val) {
        switch (val) {
            case 'internal':
            case 'external':
                this._userType = val;
                break;
            default:
                this._userType = 'all';
                break;
        }
    }

    set accountType(val) {
        switch (val) {
            case 'user':
            case 'service':
                this._accountType = val;
                break;
            default:
                this._accountType = 'all';
                break;
        }
    }

    set userIds(values) {
        this._userIds = values;
        this.onChange(this._userIds);
    }

    onChange: any = () => { };
    onTouched: any = () => { };
    validateFn: any = () => { };

    // TODO: this is only necessary once as we get users from the user picker
    // no need for an observable here
    ngOnInit() {
        this.sub = this.userIdsControl
            .valueChanges
            .subscribe(userIds => {
                return userIds && userIds.length ?
                    this.userService
                        .findUsers({ filter: `id:(${userIds.join('|')})` })
                        .toPromise()
                        .then(users => {
                            this.users = users;
                        }) :
                    Promise.resolve(() => this.users = []);
            });
    }

    ngOnDestroy() {
        if (this.sub) {
            this.sub.unsubscribe();
        }
    }

    public add(user) {
        if (user) {
            this.users = _.unionWith(this.users, [user], _.isEqual);
            this.userIds = _.map(this.users, 'id');
        }
    }

    public remove(user) {
        _.remove(this.users, o => o.id === user.id);
        this.userIds = _.map(this.users, 'id');
    }

    public writeValue(values) {
        this.userIds = values;
        if (values && values.length > 0) {
            this.userIdsControl.setValue(values);
        } else {
            this.userIdsControl.setValue(null);
        }
    }

    public registerOnChange(fn) {
        this.onChange = fn;
    }

    public registerOnTouched(fn) {
        this.onTouched = fn;
    }

    public validate(c: FormControl) {
        return this.validateFn(c);
    }

    public displayFn(user: User) {
        return user ? `${user.firstName} ${user.lastName}` : '';
    }

}
